import {CALL_API, Call} from 'redux/middleware/api'
import {getAggregatedSites, getCurrentMerchant} from 'redux/modules/currentUser/selectors'
import {getDynamicDateRange} from 'redux/modules/currentUser/utils'
import {showToast} from 'redux/modules/ui/toaster/actions'
import {reportError} from 'redux/modules/error/actions'
import {batchActions} from 'redux-batch-enhancer'
import {getMetricByKey} from 'utils/metrics'
import dimensions from 'utils/dimensions'
import json2csv from 'json2csv'
import formatDateWithTz from 'utils/formatDateWithTz'
import isEmpty from 'lodash/isEmpty'
import {
  REPORTS,
  REPORT,
  REPORT_SET_DATE_RANGE,
  REPORTS_ADD,
  REPORTS_DELETE,
  REPORTS_DATA,
  REPORTS_CHART_DATA,
  REPORTS_SET_DATA,
  REPORTS_SET_TOTAL
} from './'

const statsVersion = localStorage.getItem('statsVersion') === 'v1' ? '' : 'v2/'

function getDimension(dimensionId) {
  return dimensions.find(dimension => dimension.id === dimensionId)
}

function fetchReportsRequest({merchantSiteId, merchantId, crossSiteMode}) {
  return {
    [CALL_API]: {
      endpoint: crossSiteMode ? `merchants/${merchantId}/reports` : `sites/${merchantSiteId}/reports`,
      errorsHandled: true,
      types: REPORTS
    }
  }
}

function fetchReportRequest({merchantSiteId, merchantId, reportId, crossSiteMode}) {
  return {
    [CALL_API]: {
      endpoint: crossSiteMode
        ? `merchants/${merchantId}/reports/${reportId}`
        : `sites/${merchantSiteId}/reports/${reportId}`,
      errorsHandled: true,
      types: REPORT
    }
  }
}

function fetchReportTableDataRequest(report, merchantSiteId, targetCurrency) {
  const {
    startDate: start,
    endDate: end,
    dimensions,
    metrics,
    filter,
    timezone
  } = report

  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: REPORTS_DATA,
      queryParams: {
        merchantSiteId: merchantSiteId || report.merchantSiteId,
        range: {start, end},
        targetCurrency,
        dimensions,
        metrics,
        filter,
        timeUnit: 'total',
        timezone
      }
    }
  }
}

function fetchReportTableTotalRequest(report, merchantSiteId, targetCurrency) {
  const {
    startDate: start,
    endDate: end,
    filter,
    metrics,
    timezone
  } = report

  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: REPORTS_DATA,
      queryParams: {
        merchantSiteId,
        range: {start, end},
        metrics,
        filter,
        targetCurrency,
        timeUnit: 'total',
        timezone
      }
    }
  }
}

function loadReportChartData({
  merchantSiteId,
  reportId,
  startDate,
  endDate,
  dimensions,
  metric,
  chartIndex,
  dimensionIndex,
  targetCurrency,
  timezone
}) {
  return {
    [CALL_API]: {
      endpoint: `${statsVersion}stats`,
      types: REPORTS_CHART_DATA,
      params: {reportId: reportId, chartIndex, dimensionIndex},
      queryParams: {
        merchantSiteId,
        range: {start: startDate, end: endDate},
        dimensions: dimensions.map(dimension => Object.keys(dimension)[0]),
        metrics: metric,
        filter: dimensions,
        targetCurrency,
        timeUnit: 'day',
        timezone
      }
    }
  }
}

function deleteReportRequest({merchantSiteId, reportId}) {
  return {
    [CALL_API]: {
      endpoint: `sites/${merchantSiteId}/reports/${reportId}`,
      errorsHandled: true,
      types: REPORTS_DELETE,
      method: 'DELETE',
      params: {
        reportId
      }
    }
  }
}

function addReportRequest({
  merchantSiteId,
  merchantId,
  crossSiteMode,
  name,
  startDate,
  endDate,
  dynamicDateType,
  metrics,
  dimensions,
  selectedSites,
  filters,
  charts,
  timezone
}) {
  const bodyParams = {
    name,
    startDate: decodeURIComponent(formatDateWithTz(startDate, timezone)),
    endDate: decodeURIComponent(formatDateWithTz(endDate, timezone)),
    metrics,
    dimensions,
    selectedSites: [].concat(selectedSites),
    filter: Object.keys(filters).reduce((acc, filter) => {
      const values = filters[filter]
      return acc.concat(values.map(value => ({[filter]: value})))
    }, []),
    charts: charts.map(chart => {
      return {
        metric: chart.metric,
        dimensions: chart.dimensions
      }
    })
  }

  if (dynamicDateType) {
    bodyParams.dynamicDateType = dynamicDateType
  }
  return {
    [CALL_API]: {
      endpoint: crossSiteMode ? `merchants/${merchantId}/reports` : `sites/${merchantSiteId}/reports`,
      errorsHandled: true,
      types: REPORTS_ADD,
      method: 'POST',
      body: bodyParams
    }
  }
}

export function fetchReports(crossSiteMode) {
  return (dispatch, getState) => {
    const state = getState()
    const merchant = getCurrentMerchant(state)
    const sites = getAggregatedSites(state, crossSiteMode)
    dispatch(fetchReportsRequest({merchantSiteId: sites, merchantId: merchant.id, crossSiteMode}))
  }
}

export function fetchReport(reportId, crossSiteMode, targetCurrency, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const {id: merchantId} = getCurrentMerchant(state)
    const merchantSiteId = getAggregatedSites(state, crossSiteMode)
    dispatch(fetchReportRequest({merchantSiteId, merchantId, reportId, crossSiteMode}))
      .success(report => {
        const {today} = getState()
        const {dynamicDateType, startDate, endDate, selectedSites} = report
        const reportSiteIds = selectedSites || merchantSiteId
        const {start, end} = getDynamicDateRange(dynamicDateType, today, startDate, endDate)
        const reportWithDates = {
          ...report,
          startDate: formatDateWithTz(start, timezone),
          endDate: formatDateWithTz(end, timezone),
          timezone
        }

        dispatch(fetchReportTableTotalRequest(reportWithDates, reportSiteIds, targetCurrency))
          .success(data => {
            dispatch({
              type: REPORTS_SET_TOTAL,
              reportId: report.id,
              total: data[0]
            })
          })

        dispatch(fetchReportTableDataRequest(reportWithDates, reportSiteIds, targetCurrency, timezone))
          .success(reportData => {
            dispatch({
              type: REPORTS_SET_DATA,
              reportId: report.id,
              data: reportData
            })
          })

        dispatch(loadReportChartsData(reportSiteIds, reportWithDates, targetCurrency))
      })
  }
}

export function addReport(reportProps, crossSiteMode) {
  return (dispatch, getState) => new Call((success, fail) => {
    const state = getState()
    const merchantSiteId = getAggregatedSites(state, crossSiteMode)
    const {id: merchantId} = getCurrentMerchant(state)
    dispatch(addReportRequest({merchantSiteId, merchantId, crossSiteMode, ...reportProps}))
      .success(success)
      .fail(fail)
      .fail(dispatch(reportError(e => `Error while saving report: ${e.message}`)))
  })
}

export function deleteReport(reportId) {
  return (dispatch, getState) => new Call((success, fail) => {
    const state = getState()
    const merchantSiteId = getAggregatedSites(state)
    dispatch(deleteReportRequest({merchantSiteId, reportId}))
      .success(success)
      .success(() => dispatch(showToast(`Report has been deleted`)))
      .fail(fail)
      .fail(dispatch(reportError(e => `Error while deleting report: ${e.message}`)))
  })
}

export function setReportRange(
  reportId,
  startDate,
  endDate,
  dynamicDateType = false,
  crossSiteMode,
  targetCurrency,
  timezone
) {
  return (dispatch, getState) => {
    const state = getState()
    const {reports, today} = state
    const merchantSiteId = getAggregatedSites(state, crossSiteMode)
    const report = reports[reportId]
    const {dimensions, metrics, filter} = report

    dispatch({
      type: REPORT_SET_DATE_RANGE,
      reportId,
      startDate: formatDateWithTz(startDate, timezone),
      endDate: formatDateWithTz(endDate, timezone),
      dynamicDateType
    })

    const {start, end} = getDynamicDateRange(dynamicDateType, today, startDate, endDate)
    const setData = reportData => {
      dispatch({
        type: REPORTS_SET_DATA,
        reportId,
        data: reportData
      })
    }

    dispatch(fetchReportTableDataRequest({
      startDate: formatDateWithTz(start, timezone),
      endDate: formatDateWithTz(end, timezone),
      dimensions,
      metrics,
      filter,
      timezone
    }, merchantSiteId, targetCurrency)).success(setData)

    dispatch(fetchReportTableTotalRequest({
      startDate: formatDateWithTz(start, timezone),
      endDate: formatDateWithTz(end, timezone),
      dimensions,
      metrics,
      filter,
      timezone
    }, merchantSiteId, targetCurrency))
      .success(data => {
        dispatch({
          type: REPORTS_SET_TOTAL,
          reportId: report.id,
          total: data[0]
        })
      })

    dispatch(loadReportChartsData(merchantSiteId, {
      ...report,
      startDate: formatDateWithTz(start, timezone),
      endDate: formatDateWithTz(end, timezone),
      timezone
    }, targetCurrency))
  }
}

export function downloadCSV(report, crossSiteMode, targetCurrency, timezone) {
  return (dispatch, getState) => {
    const state = getState()
    const merchantSiteId = getAggregatedSites(state, crossSiteMode)
    const {dynamicDateType, startDate, endDate} = report
    const {start, end} = getDynamicDateRange(dynamicDateType, state.today, startDate, endDate)
    const reportWithDates = {
      ...report,
      startDate: formatDateWithTz(start, timezone),
      endDate: formatDateWithTz(end, timezone),
      timezone
    }
    dispatch(fetchReportTableDataRequest(reportWithDates, merchantSiteId, targetCurrency))
      .success(data => {
        dispatch(fetchReportTableTotalRequest(reportWithDates, merchantSiteId, targetCurrency))
          .success(total => {
            const fieldNames = []
            const fields = []
            report.dimensions.forEach(dimensionId => {
              const dimension = getDimension(dimensionId)
              fieldNames.push(dimension.label)
              fields.push(`dimensions.${dimensionId}`)
            })
            report.metrics.forEach(metricId => {
              const metric = getMetricByKey(metricId)
              fieldNames.push(metric.label)
              fields.push(metricId)
            })

            const csvHeader = 'data:text/csv;charset=utf-8,'
            const csvFile = encodeURI(csvHeader + json2csv({data: data.concat(total), fields, fieldNames}))
            const link = document.createElement('a')
            link.setAttribute('href', csvFile)
            const timestamp = new Date().getTime()
            link.setAttribute('download', `${report.name}.${timestamp}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          })
      })
      .fail(dispatch(reportError()))
  }
}

function loadReportChartsData(merchantSiteId, report, targetCurrency) {
  return dispatch => {
    const chartRequests = []
    report.charts && report.charts.forEach((chart, chartIndex) => {
      if (isEmpty(chart)) return
      chart.dimensions.forEach((dimensions, dimensionIndex) => {
        chartRequests.push(
          loadReportChartData({
            merchantSiteId,
            reportId: report.id,
            startDate: report.startDate,
            endDate: report.endDate,
            dimensions,
            targetCurrency,
            metric: chart.metric,
            chartIndex,
            dimensionIndex,
            timezone: report.timezone
          })
        )
      })
    })

    if (chartRequests.length > 0) {
      Call.all(dispatch(batchActions(chartRequests)))
        .fail(dispatch(reportError()))
    }
  }
}



// WEBPACK FOOTER //
// ./client/redux/modules/reports/actions.js